import { z } from 'zod';
import { ACCU_GROWTH_RATES } from './accumulator';
import { DIRECTIONS, MODALITIES } from './contracts';
import { BALANCE_CURRENCIES } from './fx';
import { notificationPrefsSchema } from './notifications';
import { phoneSchema } from './phone';
import { PROGRESSION_LIMITS } from './progression';

/** Monetary amounts travel as strings to avoid float rounding. */
export const decimalString = z
  .string()
  .trim()
  .regex(/^\d{1,12}(\.\d{1,8})?$/, 'Valor inválido');

export const positiveDecimal = decimalString.refine((v) => Number(v) > 0, 'Deve ser maior que zero');

export const durationUnitSchema = z.enum(['t', 's', 'm', 'h', 'd']);
export const modalitySchema = z.enum(MODALITIES);
export const directionSchema = z.enum(DIRECTIONS);
/** Deriv symbols (1HZ100V, frxEURUSD); a dash is accepted for symbols stored by the previous broker (history). */
export const symbolSchema = z.string().regex(/^[\w-]{2,30}$/);

// ---- Auth -----------------------------------------------------------------

export const registerSchema = z.object({
  name: z.string().trim().min(2).max(80),
  email: z.email().max(160).transform((v) => v.toLowerCase()),
  phone: phoneSchema,
  password: z.string().min(8, 'Mínimo de 8 caracteres').max(200),
  referralCode: z.string().trim().max(32).optional(),
  acceptTerms: z.literal(true, { error: 'É necessário aceitar os termos e o aviso de risco' }),
});

export const loginSchema = z.object({
  email: z.email().transform((v) => v.toLowerCase()),
  password: z.string().min(1).max(200),
});

export const patConnectSchema = z.object({
  token: z.string().trim().min(10).max(512),
});

// ---- Trading ----------------------------------------------------------------

export const quoteRequestSchema = z.object({
  accountId: z.string().min(1),
  modality: modalitySchema,
  direction: directionSchema,
  symbol: symbolSchema,
  stake: positiveDecimal,
  /** Ignored for round trading (the round fixes the contract length); kept for API compatibility. */
  duration: z.number().int().min(1).max(99999).optional(),
  durationUnit: durationUnitSchema.optional(),
  barrier: z.number().int().min(0).max(9).optional(),
});
export type QuoteRequest = z.infer<typeof quoteRequestSchema>;

/**
 * Traderoom quote: a Deriv Rise/Fall (CALL/PUT) that starts when bought and lasts the chosen ticks
 * (1–10) or time (15 s to 1 day; each asset's own minimum is checked by Deriv).
 */
export const freeQuoteRequestSchema = z.object({
  accountId: z.string().min(1),
  modality: z.literal('RISE_FALL').default('RISE_FALL'),
  direction: z.enum(['RISE', 'FALL']),
  symbol: symbolSchema,
  stake: positiveDecimal,
  duration: z.number().int().min(1).max(86400),
  durationUnit: z.enum(['t', 's', 'm', 'h']),
});
export type FreeQuoteRequest = z.infer<typeof freeQuoteRequestSchema>;

export const buyRequestSchema = z.object({
  quoteId: z.string().min(1),
  idempotencyKey: z.uuid(),
});

export const cancelRoundOrderSchema = z.object({ orderId: z.string().min(1) });

export const accuGrowthRateSchema = z.number().refine((v) => (ACCU_GROWTH_RATES as readonly number[]).includes(v), 'Crescimento deve ser de 1% a 5%');

/** Acumulador: bought on the click; optional take profit (profit amount that closes it). */
export const accumulatorBuySchema = z.object({
  accountId: z.string().min(1),
  symbol: symbolSchema,
  stake: positiveDecimal,
  growthRate: accuGrowthRateSchema,
  takeProfit: positiveDecimal.optional(),
  idempotencyKey: z.uuid(),
});
export type AccumulatorBuyRequest = z.infer<typeof accumulatorBuySchema>;

export const accumulatorSellSchema = z.object({ contractId: z.string().min(1) });

/** Decolagem bet: bought by the server at the round's takeoff (all bets of a round enter on the same tick). */
export const accumulatorBetSchema = accumulatorBuySchema.extend({ takeoff: z.number().int().positive() });
export type AccumulatorBetRequest = z.infer<typeof accumulatorBetSchema>;

export const accumulatorCancelSchema = z.object({ orderId: z.string().min(1) });

// ---- Automation ---------------------------------------------------------------

export const progressionSchema = z.object({
  enabled: z.boolean(),
  multiplier: decimalString.refine(
    (v) => Number(v) >= Number(PROGRESSION_LIMITS.minMultiplier) && Number(v) <= Number(PROGRESSION_LIMITS.maxMultiplier),
    'Multiplicador fora dos limites',
  ),
  maxLevels: z.number().int().min(0).max(PROGRESSION_LIMITS.maxLevels),
  onMaxLevel: z.enum(['RESET', 'STOP']),
});

export const automationConfigSchema = z.object({
  name: z.string().trim().min(2).max(60),
  accountId: z.string().min(1),
  symbol: symbolSchema,
  modality: modalitySchema,
  strategyKey: z.string().min(1).max(64),
  strategyParams: z.record(z.string(), z.unknown()),
  baseStake: positiveDecimal,
  /** Bots trade one entry per round; these are fixed by the round clock. */
  duration: z.number().int().min(1).max(86400).default(30),
  durationUnit: durationUnitSchema.default('s'),
  targetProfit: positiveDecimal,
  maxLoss: positiveDecimal,
  /** Safety ceiling only; sessions end at their meta / loss limit long before. */
  maxTrades: z.number().int().min(1).max(1_000_000),
  maxConcurrent: z.number().int().min(1).max(5),
  progression: progressionSchema,
});
export type AutomationConfigInput = z.infer<typeof automationConfigSchema>;

export const startSessionSchema = z.object({
  configurationId: z.string().min(1),
  /** The user must explicitly acknowledge the worst-case exposure shown. */
  acknowledgedExposure: decimalString,
  acknowledgeRisk: z.literal(true),
});

// ---- Risk -------------------------------------------------------------------

export const riskLimitsSchema = z.object({
  maxStake: positiveDecimal,
  maxExposure: positiveDecimal,
  maxDailyLoss: positiveDecimal,
  maxDailyTrades: z.number().int().min(1).max(20000),
  maxConcurrent: z.number().int().min(1).max(20),
  maxConsecutiveErrors: z.number().int().min(1).max(20),
});

// ---- History ----------------------------------------------------------------

export const periodSchema = z.enum(['today', 'yesterday', '7d', '30d', 'all', 'custom']);
export type Period = z.infer<typeof periodSchema>;

export const historyQuerySchema = z.object({
  period: periodSchema.default('7d'),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  accountId: z.string().optional(),
  modality: modalitySchema.optional(),
  symbol: symbolSchema.optional(),
  result: z.enum(['WON', 'LOST', 'OPEN']).optional(),
  source: z.enum(['MANUAL', 'AUTOMATION']).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
}).refine((q) => !q.from || !q.to || q.from <= q.to, { message: 'Período inválido', path: ['to'] });
export type HistoryQuery = z.infer<typeof historyQuerySchema>;

// ---- Profile ----------------------------------------------------------------

export const profileUpdateSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  avatarUrl: z.url().max(500).nullable().optional(),
  preferences: z
    .object({
      sound: z.boolean().optional(),
      confirmTrades: z.boolean().optional(),
      defaultSymbol: symbolSchema.optional(),
      defaultStake: positiveDecimal.optional(),
      balanceCurrency: z.enum(BALANCE_CURRENCIES).optional(),
      notifications: notificationPrefsSchema.optional(),
    })
    .optional(),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(8).max(200),
});

export const forgotPasswordSchema = z.object({
  email: z.email().max(160).transform((v) => v.toLowerCase()),
});

/** Token of a single-use link (password reset, e-mail confirmation). */
export const linkTokenSchema = z.object({ token: z.string().min(20).max(100) });

export const resetPasswordSchema = linkTokenSchema.extend({
  password: z.string().min(8, 'Mínimo de 8 caracteres').max(200),
});

/** 6 digits from the authenticator app, or a recovery code (ABCDE-FGHIJ). */
export const twoFactorCodeSchema = z.object({ code: z.string().trim().min(6).max(20) });

export const twoFactorLoginSchema = twoFactorCodeSchema.extend({ ticket: z.string().min(20).max(100) });
